import React from 'react';
import { SectionDivider } from '../components/SectionDivider';
import { MessageCircle, ArrowRight } from 'lucide-react';
interface ServicePageLayoutProps {
  title: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  content: React.ReactNode;
  faqs: {
    q: string;
    a: string;
  }[];
  relatedServices: {
    title: string;
    href: string;
  }[];
}
export function ServicePageLayout({
  title,
  metaDescription,
  heroTitle,
  heroSubtitle,
  content,
  faqs,
  relatedServices
}: ServicePageLayoutProps) {
  // In a real app, we'd inject meta tags here, but for this task we just render the content
  return <div className="min-h-screen bg-[#050505] text-white pt-24" data-id="element-1015">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden" data-id="element-1016">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#c9a84c]/5 to-transparent pointer-events-none" data-id="element-1017" />
        <div className="max-w-4xl mx-auto text-center relative z-10 reveal" data-id="element-1018">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1019">
            {heroTitle}
          </h1>
          <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto" data-id="element-1020">
            {heroSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1021">
            <a href="https://wa.me/36707559881?text=Hi%2C%20I%27m%20interested%20in%20a%20website%20for%20my%20business." target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg shadow-green-900/20" data-id="element-1022">
              <MessageCircle className="w-6 h-6" data-id="element-1023" />
              <span data-id="element-1024">Get Started on WhatsApp</span>
            </a>
            <a href="/contact" onClick={e => {
            e.preventDefault();
            window.history.pushState({}, '', '/contact');
            window.dispatchEvent(new PopStateEvent('popstate'));
            window.scrollTo(0, 0);
          }} className="flex items-center justify-center space-x-2 border border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10 px-8 py-4 rounded-lg font-medium text-lg transition-all" data-id="element-1025">
              <span data-id="element-1026">Get a Quote</span>
              <ArrowRight className="w-5 h-5" data-id="element-1027" />
            </a>
          </div>
        </div>
      </section>

      <SectionDivider data-id="element-1028" />

      {/* Main Content */}
      <section className="py-16 px-4 md:px-6" data-id="element-1029">
        <div className="max-w-3xl mx-auto prose prose-invert prose-lg reveal" data-id="element-1030">
          {content}
        </div>
      </section>

      <SectionDivider data-id="element-1031" />

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-6 bg-[#080808]" data-id="element-1032">
        <div className="max-w-3xl mx-auto reveal" data-id="element-1033">
          <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1034">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6" data-id="element-1035">
            {faqs.map((faq, idx) => <div key={idx} className="border-b border-white/10 pb-6" data-id="element-1036">
                <h3 className="text-xl font-medium text-[#c9a84c] mb-2" data-id="element-1037">
                  {faq.q}
                </h3>
                <p className="text-white/70" data-id="element-1038">{faq.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 px-4 md:px-6" data-id="element-1039">
        <div className="max-w-4xl mx-auto text-center reveal" data-id="element-1040">
          <h2 className="text-2xl font-bold mb-8" data-id="element-1041">Other Industries We Serve</h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1042">
            {relatedServices.map((service, idx) => <a key={idx} href={service.href} onClick={e => {
            e.preventDefault();
            window.history.pushState({}, '', service.href);
            window.dispatchEvent(new PopStateEvent('popstate'));
            window.scrollTo(0, 0);
          }} className="px-6 py-3 border border-white/10 rounded-full hover:border-[#c9a84c] hover:text-[#c9a84c] transition-colors" data-id="element-1043">
                {service.title}
              </a>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 md:px-6 bg-[#0a0a0a] text-center" data-id="element-1044">
        <div className="max-w-2xl mx-auto reveal" data-id="element-1045">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1046">
            Ready to Launch Your Website?
          </h2>
          <p className="text-white/60 mb-8 text-lg" data-id="element-1047">
            Join other Dublin businesses growing online with our affordable €249
            package.
          </p>
          <a href="/contact" onClick={e => {
          e.preventDefault();
          window.history.pushState({}, '', '/contact');
          window.dispatchEvent(new PopStateEvent('popstate'));
          window.scrollTo(0, 0);
        }} className="inline-block bg-[#c9a84c] text-black font-bold px-8 py-4 rounded-lg hover:bg-[#b09242] transition-colors" data-id="element-1048">
            Get Your Free Quote
          </a>
        </div>
      </section>
    </div>;
}